import { useQuery } from '@tanstack/react-query';
import { orpc } from '../../apis/orpc-query';
import { Notice } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Spinner';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { weekDateLabel } from '../tracker/tracker-display';
import { AgeCard } from './AgeCard';
import { BoardHeader } from './BoardHeader';
import { AGES_CLOSING_NOTE, DIRECTION_INTRO } from './content/ages';
import { NORTH_STAR, NORTH_STAR_TITLE } from './content/north-star';
import { ROADMAP, ROADMAP_CAPTION, ROADMAP_COLUMNS, ROADMAP_TITLE, type RoadmapLane } from './content/roadmap';

const RANK_EMPHASIS: Record<string, string> = { 'Top 1': 'text-foreground', 'Top 3': 'text-foreground/80', 'Top 5': 'text-foreground/70' };

const AGE_LANES: readonly (keyof Pick<RoadmapLane, 'bronze' | 'silver' | 'golden'>)[] = ['bronze', 'silver', 'golden'];

/**
 * Page 01 — Direction & Ages (plan §3.3, §7). North Star tiles and the roadmap are static copy; the three Age cards
 * are live from `board.direction`: the gates, units and revenue to date, the last-4-weeks run-rate and % of gate,
 * read through the board bindings — rebinding `VOLUME_DELIVERED` changes the Bronze card on the next load.
 */
export const DirectionPage = () => {
  const { t } = useI18nTranslation();
  const direction = useQuery(orpc.board.direction.queryOptions({ input: {} }));

  return (
    <div
      className="space-y-8"
      data-board-page="direction"
    >
      <BoardHeader {...DIRECTION_INTRO} />

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{NORTH_STAR_TITLE}</h2>
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
          {NORTH_STAR.map((item) => (
            <li
              key={item.name}
              className="rounded-md border border-border bg-card px-3 py-2.5"
              data-north-star={item.name}
            >
              <p className={`text-lg font-semibold ${RANK_EMPHASIS[item.rank] ?? ''}`}>{item.rank}</p>
              <p className="text-sm font-medium text-foreground">{item.name}</p>
              <p className="text-xs text-muted-foreground">{item.note}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        {direction.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('app.loading')}
          </div>
        ) : direction.isError ? (
          <Notice tone="danger">{serverMessage(direction.error) ?? t('board.loadFailed')}</Notice>
        ) : (
          <>
            <p
              className="text-xs text-muted-foreground"
              data-as-of
            >
              {direction.data.asOfWeek
                ? t('board.direction.asOf', { week: weekDateLabel(direction.data.asOfWeek.weekDate) })
                : t('board.direction.noCurrentWeek')}
            </p>
            <div className="grid gap-4 lg:grid-cols-3">
              {direction.data.ages.map((card) => (
                <AgeCard
                  key={card.age}
                  card={card}
                  unbound={direction.data.unbound}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{ROADMAP_CAPTION}</p>
          <h2 className="text-lg font-semibold text-foreground">{ROADMAP_TITLE}</h2>
        </div>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[48rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="w-48 px-4 py-2" />
                {ROADMAP_COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="px-4 py-2 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROADMAP.map((lane) => (
                <tr
                  key={lane.name}
                  className="border-b border-border align-top last:border-b-0"
                  data-roadmap-lane={lane.name}
                >
                  <th
                    scope="row"
                    className="px-4 py-3 text-start"
                  >
                    <p className="font-medium text-foreground">{lane.name}</p>
                    <p className="text-xs font-normal text-muted-foreground">{lane.who}</p>
                  </th>
                  {AGE_LANES.map((age) => (
                    <td
                      key={age}
                      className="px-4 py-3"
                    >
                      <ul className="list-disc space-y-1 ps-4 text-xs leading-relaxed text-muted-foreground">
                        {lane[age].map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-4xl text-xs leading-relaxed text-muted-foreground">{AGES_CLOSING_NOTE}</p>
      </section>
    </div>
  );
};
